import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit, Trash2, X, Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import ImageUploader from '../../components/ImageUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyItem = {
  category: 'residential',
  images: [''],
  translations: {
    sk: { title: '', description: '' },
    en: { title: '', description: '' },
    de: { title: '', description: '' },
    cs: { title: '', description: '' },
    ru: { title: '', description: '' }
  }
};

const AdminGallery = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('sk');

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gallery`);
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
      toast.error(t('admin.gallery_page.fetch_error', 'Chyba pri načítaní galérie'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm(t('admin.gallery_page.delete_confirm', 'Naozaj chcete zmazať túto položku?'))) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/gallery/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setItems(items.filter(i => i.id !== itemId));
        toast.success(t('admin.gallery_page.deleted', 'Položka bola zmazaná'));
      } else {
        toast.error(t('admin.gallery_page.delete_error', 'Chyba pri mazaní položky'));
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Chyba pri mazaní položky');
    }
  };

  const handleSave = async (item) => {
    setSaving(true);
    try {
      const url = isCreating 
        ? `${API_URL}/api/admin/gallery`
        : `${API_URL}/api/admin/gallery/${item.id}`;
      
      const response = await fetch(url, {
        method: isCreating ? 'POST' : 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(item)
      });
      
      if (response.ok) {
        fetchItems();
        setEditingItem(null);
        setIsCreating(false);
        toast.success(isCreating ? t('admin.gallery_page.created', 'Položka bola vytvorená') : t('admin.gallery_page.updated', 'Položka bola aktualizovaná'));
      } else {
        const err = await response.json();
        toast.error(err.detail || t('admin.gallery_page.save_error', 'Chyba pri ukladaní'));
      }
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error(t('admin.gallery_page.save_error_full', 'Chyba pri ukladaní položky'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingItem({ ...emptyItem });
    setActiveTab('sk');
  };

  const languages = [
    { code: 'sk', label: 'SK' },
    { code: 'en', label: 'EN' },
    { code: 'de', label: 'DE' },
    { code: 'cs', label: 'CS' },
    { code: 'ru', label: 'RU' }
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">{t('admin.gallery_page.title', 'Galéria')}</h1>
        <Button 
          className="bg-orange-500 hover:bg-orange-600"
          onClick={handleCreate}
          data-testid="add-gallery-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('admin.gallery_page.add', 'Pridať položku')}
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-gray-500">{t('admin.gallery_page.empty', 'Žiadne položky v galérii')}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map(item => (
              <div key={item.id} className="border rounded-lg overflow-hidden group" data-testid={`gallery-item-${item.id}`}>
                <div className="aspect-video relative">
                  <img
                    src={item.images?.[0] || 'https://via.placeholder.com/400x300'}
                    alt={item.translations?.sk?.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => { setIsCreating(false); setEditingItem(item); setActiveTab('sk'); }}
                      data-testid={`edit-gallery-${item.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(item.id)}
                      data-testid={`delete-gallery-${item.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-medium">{item.translations?.sk?.title}</h3>
                  <p className="text-sm text-gray-500 line-clamp-2">
                    {item.translations?.sk?.description}
                  </p>
                  <span className="inline-block mt-2 px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
                    {item.category}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold">
                {isCreating ? t('admin.gallery_page.new', 'Nová položka') : t('admin.gallery_page.edit', 'Upraviť položku')}
              </h2>
              <button onClick={() => { setEditingItem(null); setIsCreating(false); }}>
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Category */}
              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.gallery_page.category', 'Kategória')}</label>
                <select
                  className="w-full px-3 py-2 border rounded-md"
                  value={editingItem.category || 'residential'}
                  onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                  data-testid="gallery-category-select"
                >
                  <option value="residential">{t('admin.gallery_page.cat_residential', 'Rezidenčné')}</option>
                  <option value="commercial">{t('admin.gallery_page.cat_commercial', 'Komerčné')}</option>
                  <option value="automotive">{t('admin.gallery_page.cat_automotive', 'Automobilové')}</option>
                </select>
              </div>

              {/* Image Upload */}
              <ImageUploader
                token={token}
                currentImage={editingItem.images?.[0] || ''}
                onImageUploaded={(url) => setEditingItem({ ...editingItem, images: [url] })}
                label={t('admin.gallery_page.project_image', 'Obrázok projektu')}
              />

              {/* Language Tabs */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Preklad</h3>
                <div className="flex gap-2 mb-4">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => setActiveTab(lang.code)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                        activeTab === lang.code
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('admin.gallery_page.field_title', 'Názov')} ({activeTab.toUpperCase()})</label>
                    <Input
                      value={editingItem.translations?.[activeTab]?.title || ''}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        translations: {
                          ...editingItem.translations,
                          [activeTab]: { ...editingItem.translations?.[activeTab], title: e.target.value }
                        }
                      })}
                      placeholder={t('admin.gallery_page.title_ph', 'Názov projektu')}
                      data-testid={`gallery-title-${activeTab}`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Popis ({activeTab.toUpperCase()})</label>
                    <textarea
                      className="w-full px-3 py-2 border rounded-md min-h-[100px]"
                      value={editingItem.translations?.[activeTab]?.description || ''}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        translations: {
                          ...editingItem.translations,
                          [activeTab]: { ...editingItem.translations?.[activeTab], description: e.target.value }
                        }
                      })}
                      placeholder="Popis projektu"
                      data-testid={`gallery-description-${activeTab}`}
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => { setEditingItem(null); setIsCreating(false); }}>
                  {t('common.cancel', 'Zrušiť')}
                </Button>
                <Button 
                  className="bg-orange-500 hover:bg-orange-600"
                  onClick={() => handleSave(editingItem)}
                  disabled={saving}
                  data-testid="save-gallery-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="animate-spin mr-2 h-4 w-4" />
                      {t('admin.gallery_page.saving', 'Ukladám...')}
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {isCreating ? t('common.create', 'Vytvoriť') : t('common.save', 'Uložiť')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
